import React from "react";
import type { DeviceFormData } from "../lib/device";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


interface AddSNMPProps {
  formData: DeviceFormData;
  setFormData: React.Dispatch<React.SetStateAction<DeviceFormData>>;
  errors: Partial<Record<keyof DeviceFormData, string>>;
}

function AddSNMP({ formData, setFormData, errors }: AddSNMPProps) {
  const handleChange = (field: keyof DeviceFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">SNMP Configuration</h2>
      <div className="space-y-4">
        {/* SNMP fields remain unchanged */}
        <div>
          <Label htmlFor="snmpVersion">SNMP Version</Label>
          <Select value={formData.snmpVersion} onValueChange={(v) => handleChange("snmpVersion", v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2c">2c</SelectItem>
              <SelectItem value="3">3</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="snmpPort">Port</Label>
          <Input
            id="snmpPort"
            type="number"
            min="1"
            max="65535"
            value={formData.snmpPort}
            onChange={(e) => handleChange("snmpPort", e.target.value)}
            placeholder="161"
            className={errors.snmpPort ? "border-red-500" : ""}
          />
          {errors.snmpPort && <p className="text-sm text-red-500">{errors.snmpPort}</p>}
        </div>
        {(formData.snmpVersion === "1" || formData.snmpVersion === "2c") && (
          <div>
            <Label htmlFor="snmpCommunity">Community String</Label>
            <Input
              id="snmpCommunity"
              value={formData.snmpCommunity}
              onChange={(e) => handleChange("snmpCommunity", e.target.value)}
              placeholder="public"
              className={errors.snmpCommunity ? "border-red-500" : ""}
            />
            {errors.snmpCommunity && <p className="text-sm text-red-500">{errors.snmpCommunity}</p>}
          </div>
        )}
        {formData.snmpVersion === "3" && (
          <>
            <div>
              <Label htmlFor="snmpUsername">Username</Label>
              <Input
                id="snmpUsername"
                value={formData.snmpUsername}
                onChange={(e) => handleChange("snmpUsername", e.target.value)}
                placeholder="SNMP username"
                className={errors.snmpUsername ? "border-red-500" : ""}
              />
              {errors.snmpUsername && <p className="text-sm text-red-500">{errors.snmpUsername}</p>}
            </div>
            <div>
              <Label htmlFor="snmpAuthProtocol">Authentication Protocol</Label>
              <Select value={formData.snmpAuthProtocol} onValueChange={(v) => handleChange("snmpAuthProtocol", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MD5">MD5</SelectItem>
                  <SelectItem value="SHA">SHA</SelectItem>
                  <SelectItem value="SHA-224">SHA-224</SelectItem>
                  <SelectItem value="SHA-256">SHA-256</SelectItem>
                  <SelectItem value="SHA-384">SHA-384</SelectItem>
                  <SelectItem value="SHA-512">SHA-512</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="snmpAuthPassword">Authentication Password</Label>
              <Input
                id="snmpAuthPassword"
                type="password"
                value={formData.snmpAuthPassword}
                onChange={(e) => handleChange("snmpAuthPassword", e.target.value)}
                placeholder="Password (min 8 chars)"
                className={errors.snmpAuthPassword ? "border-red-500" : ""}
              />
              {errors.snmpAuthPassword && <p className="text-sm text-red-500">{errors.snmpAuthPassword}</p>}
            </div>
            <div>
              <Label htmlFor="snmpPrivProtocol">Encryption (Privacy Protocol)</Label>
              <Select value={formData.snmpPrivProtocol} onValueChange={(v) => handleChange("snmpPrivProtocol", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DES">DES</SelectItem>
                  <SelectItem value="3DES">3DES</SelectItem>
                  <SelectItem value="AES">AES</SelectItem>
                  <SelectItem value="AES-192">AES-192</SelectItem>
                  <SelectItem value="AES-256">AES-256</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="snmpPrivPassword">Encryption Password (Optional)</Label>
              <Input
                id="snmpPrivPassword"
                type="password"
                value={formData.snmpPrivPassword}
                onChange={(e) => handleChange("snmpPrivPassword", e.target.value)}
                placeholder="Encryption password (min 8 chars)"
                className={errors.snmpPrivPassword ? "border-red-500" : ""}
              />
              {errors.snmpPrivPassword && <p className="text-sm text-red-500">{errors.snmpPrivPassword}</p>}
            </div>
          </>
        )}
        <div>
          <Label htmlFor="snmpOid">OID</Label>
          <Input
            id="snmpOid"
            value={formData.snmpOid || ''}
            onChange={(e) => handleChange("snmpOid", e.target.value)}
            placeholder="1.3.6.1.2.1.1.1.0"
            className={errors.snmpOid ? "border-red-500" : ""}
          />
          {errors.snmpOid && <p className="text-sm text-red-500">{errors.snmpOid}</p>}
        </div>
      </div>
    </div>
  );
}

export default AddSNMP;